import { Component, computed, DestroyRef, effect, EffectRef, ElementRef, inject, signal, Signal, ViewChild, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { ButtonComponent } from '@components/shared/button/button.component';
import { isInIframe } from '@helpers/utils/is-in-iframe.util';
import { ButtonType } from '@models/enums/components/Button/ButtonType.enum';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { View3dDisplay } from '@models/types/components/View3dDisplay.type';
import { AmenitiesService } from '@services/amenities.service';
import { RelatedPreviewsService } from '@services/components/related-previews.service';
import { DvmService } from '@services/dvm.service';
import { TranslationService } from '@services/translation.service';
import { UiService } from '@services/ui.service';
import anime from 'animejs';
import { ViewerRouteParamsService } from '@services/viewer-route-params.service';

@Component({
    selector: 'app-hyperlink',
    imports: [
        ButtonComponent
    ],
    templateUrl: './hyperlink.component.html',
    styleUrl: './hyperlink.component.css'
})
export class HyperlinkComponent {

  @ViewChild('hyperlinkBtn', { read: ElementRef })
  private readonly hyperlinkBtn!: ElementRef<HTMLElement>;

  @ViewChild('urlInput')
  private readonly urlInput!: ElementRef<HTMLInputElement>;

  // SERVICES
  private readonly dvmService:      DvmService             = inject(DvmService);
  private readonly router:          Router                 = inject(Router);
  private readonly route:           ActivatedRoute         = inject(ActivatedRoute);
  private readonly routeParams:     ViewerRouteParamsService = inject(ViewerRouteParamsService);
  private readonly destroyRef:      DestroyRef             = inject(DestroyRef);
  private readonly ams:             AmenitiesService       = inject(AmenitiesService);
  private readonly relatedPreviews: RelatedPreviewsService = inject(RelatedPreviewsService);
  public  readonly translation:     TranslationService     = inject(TranslationService);
  
  

  // CONFIG
    private readonly uiconfig:              Signal<UiConfiguration>  = inject(UiService).uiConfig;
    private readonly view3dDisplay:         Signal<View3dDisplay>    = computed((): View3dDisplay=> this.uiconfig().View3dDisplay);
  

  // GLOBAL STATE
  public readonly  view:          Signal<AppViews| null> = this.dvmService.view;
  protected readonly hasViewId:   boolean                = this.routeParams.hasViewId;


  // BUTTON PROPERTIES
  public readonly  type:          ButtonType             = ButtonType.Default;
  public readonly  active:        WritableSignal<boolean> = signal(false);

  // IFRAME
  public  readonly isIframe:      boolean                = isInIframe();
  public readonly  showUrlPanel:  WritableSignal<boolean> = signal(false);
  public readonly  generatedUrl:  WritableSignal<string> = signal('');
  public readonly  copied:        WritableSignal<boolean> = signal(false);

  // COMPONENT STATE
  public readonly  showElement:   WritableSignal<boolean> = signal(this.view() === AppViews.Pano);
  public readonly  showLabel:     WritableSignal<boolean> = signal(false);
  private readonly timer:         number                 = 1500
  private readonly animation:     number                 = 300;
  private animationTimer:         ReturnType<typeof setTimeout> | null = null;
  private activeTimer:            ReturnType<typeof setTimeout> | null = null;

  // EFFECTS
  public readonly  onViewChange:  EffectRef              = effect((): void => {

    const view: AppViews | null = this.view(),
          show: boolean = view === AppViews.Pano && this.view3dDisplay().enabled && this.view3dDisplay().copySeatLink && !this.hasViewId;

    if(view === null) return;

    if (!this.showElement()) {
      this.clearAnimationTimer();
      this.animationTimer = window.setTimeout((): void => {
        this.animateHyperlink(show);
        this.animationTimer = null;
      }, 300);
    } 
    else {
      this.animateHyperlink(show);
    }
    
  });

  constructor() {
    this.destroyRef.onDestroy((): void => {
      this.clearAnimationTimer();
      if(this.activeTimer) clearTimeout(this.activeTimer);
    });
  }


  // METHODS
  /**
   * Genera un enlace a la vista 3D actual y lo copia al portapapeles.
   * Release notes (0.11.1): Pasamos este método a ser async para mejorar el refactor y evitar que si la copia falla, 
   * la animacion se quede en un estado activo. 
   */
  public async copyLink(): Promise<void> {

    if (this.isIframe) return this.manageIframe();

    this.togleActiveState(true);

    try {
      await navigator.clipboard.writeText(this.generateUrl());
    } 
    catch (error) {
      console.error('Failed to copy: ', error);
    }
    finally {
      if(this.activeTimer) clearTimeout(this.activeTimer);
      this.activeTimer = window.setTimeout((): void => {
        this.togleActiveState(false);
        this.activeTimer = null;
      }, this.timer + this.animation);
    }

    
  }

  public async copyFromInput(): Promise<void> {

    try {

      await navigator.clipboard.writeText(this.generatedUrl());

      this.copied.set(true);

      return;
      
    } catch { /* Fallback */ }

    // Fallback: select + execCommand (funciona en iframes cross-origin)
    // https://stackoverflow.com/a/72645682
    try {

      this.urlInput.nativeElement.select();

      const success: boolean = document.execCommand('copy');

      if (success) this.copied.set(true);

    } catch (error) {
      console.error('Failed to copy from input: ', error);
    }

  }

  public closeUrlPanel(): void {
    this.showUrlPanel.set(false);
    this.copied.set(false);
  }

  public forceClose(): void{
    this.togleActiveState(false);
  }

  /**
   * Genera la URL para la vista 3D actual.
   * La URL se genera a partir del ID de la vista 3D actual y los parámetros de consulta proporcionaods.
   * @returns La URL generada.
   */
  private generateUrl(): string {

    const view3dId: string  = btoa(this.dvmService.viewer3d.getViewId()!),
          amenities: string = btoa(this.ams.selectedAmenities().map(a => a.id.toString()).join(',')),
          relatedPreview: string = btoa(this.relatedPreviews.currentPreview()?.label || '') ,
          urlTree:  UrlTree = this.router.createUrlTree(
            [], 
            {
              relativeTo: this.route,
              queryParams: {
                view_id: view3dId,
                amenities: amenities.length ? amenities : null,
                related_preview: relatedPreview ? relatedPreview : null
              }
            }
          ),
          finalUrl: string  = location.origin + this.router.serializeUrl(urlTree);

    return finalUrl;
     
  }

  private manageIframe(): void {
    
    this.generatedUrl.set(this.generateUrl());
    
    this.showUrlPanel.set(true);
    
    requestAnimationFrame((): void => this.urlInput?.nativeElement.select());
    
    return;
  }

  /**
   * Método que anima el botón de enlace para mostrar u ocultar el elemento.
   * @param show 
   */
  private animateHyperlink(show: boolean): void {
    const hyperlinkBtn: HTMLElement | undefined = this.hyperlinkBtn?.nativeElement;
    if(!hyperlinkBtn) return;
    
    anime({
      begin:    (): void => { if(show && !this.showElement()) this.showElement.set(true); },
      complete: (): void => { if(!show && this.showElement()) this.showElement.set(false); },
      targets:  hyperlinkBtn,
      opacity:  show ? 1 : 0,
      duration: 300,
      easing:   'easeInOutQuad',
    })

  }

  /**
   * Método que alterna el estado activo del botón de enlace y anima su transición.
   * La animación depende del estado que se le pase, si es activo se ensanchará y si no retornará a su tamaño original.
   * El funcionamiento es el siguiente:
   * 1. Nada más comenzar, si el estado es true, se activa inmediatamente el estado activo del botón.
   * 2. Durante la animación, se comprueba si el estado es true o false para mostrar u ocultar el texto del botón.
   *  - Si es true, se espera a que la animación alcance un 80% de su progreso para mostrar el texto.
   *  - Si es false, se espera a que la animación alcance un 20% de su progreso para ocultar el texto.
   * 3. Al finalizar la animación, si es false, se desactiva el estado activo del botón. (Una vez vuelta a su tamaño original).
   * @param state Estado activo del botón.
   */

  private togleActiveState(state: boolean): void {
    const hyperlinkBtn: HTMLElement | undefined = this.hyperlinkBtn?.nativeElement;
    if(!hyperlinkBtn) return;

    const targets: HTMLButtonElement = hyperlinkBtn.children[0] as HTMLButtonElement;

    anime({
      begin: (): void => {
        if (state) this.active.set(true);
      },
      update: (animation): void => {
        const threshold: number = state ? 80 : 20;
        if (animation.progress >= threshold) this.showLabel.set(state);
      },
      complete: (): void => {
        if (!state) this.active.set(false);
      },
      targets,
      width: state ? 145 : 34,
      easing: 'easeInOutQuad',
      duration: this.animation,
    });
  }

  private clearAnimationTimer(): void {
    if(!this.animationTimer) return;
    clearTimeout(this.animationTimer);
    this.animationTimer = null;
  }

}
